/**
 * Participants editor — "Split back" an AUTOMATIC merge. On a standalone
 * desktop call the services may decide two diarized voices are one person and
 * merge them during the call; every line it moved keeps `autoMergedFrom:
 * <source id>`. The admin can split such a merge back out: the source returns
 * as its own participant with a name and role (customer by default).
 * Pure and React-free: both desktop apps implement the same rules, and the
 * server enforces them again (it refuses a split anywhere else).
 *
 * Rules:
 * - standalone desktop calls only (bot calls never auto-merge);
 * - one automatic merge per source id, shown under the row its lines went into;
 * - a source that is already a participant row has nothing to split;
 * - a split-back row takes no "Same as…" and is never a merge target (no chains);
 * - while split, the target row's turn count drops by the split lines.
 */
import { canMergeParticipants } from '../../constants/participantMerge';
import { ParticipantRole, SpeakerLike } from '../../config/participants';
import { ParticipantDraft, ParticipantEdit, ParticipantRow } from './participantRows';

/** A saved transcript line; `autoMergedFrom` is set on lines an automatic merge moved. */
export type AutoMergeLine = SpeakerLike & { text?: string; autoMergedFrom?: string | number | null };

export interface AutoMerge {
  /** The voice that was merged away (the id a split restores). */
  sourceId: string;
  /** The participant its lines now belong to. */
  targetId: string;
  /** The first non-empty moved line (untruncated); empty when none has text. */
  firstLine: string;
  lineCount: number;
}

/** Split-back rows being edited: source id -> the name and role it will get. */
export type SplitMap = Readonly<Record<string, ParticipantDraft>>;

const CUSTOMER_ID_PATTERN = /^customer_(\d+)$/;

const hasId = (value: unknown): boolean => value !== undefined && value !== null && value !== '';

/**
 * Every automatic merge on the call, grouped by `autoMergedFrom`, in the order
 * their first line appears. Empty unless the session is standalone desktop.
 */
export function findAutoMerges(
  transcripts: AutoMergeLine[] | undefined,
  rows: ParticipantRow[],
  sessionSource: string | undefined | null,
): AutoMerge[] {
  if (!canMergeParticipants(sessionSource)) return [];
  const rowIds = new Set(rows.map((row) => row.participantId));
  const merges = new Map<string, AutoMerge>();
  (transcripts ?? []).forEach((line) => {
    if (!hasId(line?.autoMergedFrom) || !hasId(line?.participantId)) return;
    const sourceId = String(line.autoMergedFrom);
    const text = typeof line?.text === 'string' ? line.text.trim() : '';
    const existing = merges.get(sourceId);
    if (existing) {
      existing.lineCount += 1;
      if (!existing.firstLine && text) existing.firstLine = text;
      return;
    }
    merges.set(sourceId, { sourceId, targetId: String(line.participantId), firstLine: text, lineCount: 1 });
  });
  return Array.from(merges.values()).filter(
    (merge) => !rowIds.has(merge.sourceId) && rowIds.has(merge.targetId),
  );
}

/** The automatic merges shown under one target row. */
export const autoMergesInto = (autoMerges: AutoMerge[], targetId: string): AutoMerge[] =>
  autoMerges.filter((merge) => merge.targetId === targetId);

/** Prefilled name: "Customer N+1" for a `customer_N` id (as the server defaults), else empty. */
export function defaultSplitName(sourceId: string): string {
  const match = CUSTOMER_ID_PATTERN.exec(sourceId);
  return match ? `Customer ${Number(match[1]) + 1}` : '';
}

export const isSplit = (splits: SplitMap, sourceId: string): boolean =>
  Object.prototype.hasOwnProperty.call(splits, sourceId);

/** Split an automatic merge back out; an unknown or already split source leaves the map unchanged. */
export function splitBack(autoMerges: AutoMerge[], splits: SplitMap, sourceId: string): SplitMap {
  if (isSplit(splits, sourceId) || !autoMerges.some((merge) => merge.sourceId === sourceId)) return splits;
  return { ...splits, [sourceId]: { name: defaultSplitName(sourceId), role: ParticipantRole.CUSTOMER } };
}

/** Change a split row's name or role. */
export function updateSplit(splits: SplitMap, sourceId: string, change: Partial<ParticipantDraft>): SplitMap {
  if (!isSplit(splits, sourceId)) return splits;
  return { ...splits, [sourceId]: { ...splits[sourceId], ...change } };
}

/** Collapse a split row back into its automatic merge. */
export function undoSplit(splits: SplitMap, sourceId: string): SplitMap {
  if (!isSplit(splits, sourceId)) return splits;
  const { [sourceId]: _undone, ...remaining } = splits;
  return remaining;
}

/** Lines leaving `targetId` because their automatic merge is split back. */
export const splitTurnsOutOf = (autoMerges: AutoMerge[], splits: SplitMap, targetId: string): number =>
  autoMergesInto(autoMerges, targetId)
    .filter((merge) => isSplit(splits, merge.sourceId))
    .reduce((total, merge) => total + merge.lineCount, 0);

/** Whether any split row will come back as a salesperson (counts for the salesperson rule). */
export const splitsAddASalesperson = (splits: SplitMap): boolean =>
  Object.values(splits).some((draft) => draft?.role === ParticipantRole.SALESPERSON);

/** One `{ participantId, splitOut: true, name?, role }` per split row; an empty name lets the server default it. */
export function splitEdits(autoMerges: AutoMerge[], splits: SplitMap): ParticipantEdit[] {
  return autoMerges
    .filter((merge) => isSplit(splits, merge.sourceId))
    .map((merge) => {
      const draft = splits[merge.sourceId];
      const name = draft.name.trim();
      return { participantId: merge.sourceId, splitOut: true as const, ...(name ? { name } : {}), role: draft.role };
    });
}
